/**
 * DraggableCollapsibleFormBlock
 *
 * This form block component needs to be in its own file (rather than being rendered
 * inline in formbuilder.utilities.tsx) because it uses the React hook `useDraggableSearch`.
 * React hooks can only be called inside React components, not regular functions.
 *
 * Attempting to call `useDraggableSearch` in a render function like `renderDraggableCollapsibleFormGroup`
 * would violate the Rules of Hooks and result in error #310:
 * "Element type is invalid - expected a string or a class/function but got undefined."
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { renderIt } from '@/formbuilder/FormElementRenderer';
import { CollapsibleFormBlock } from '@/formbuilder/formBuildingBlocks';
import { useDraggableSearch } from '@/formbuilder/hooks/useDraggableSearch.hook';
import { scrollToTopOfScrollParent } from '@/utilities/dom.utilities';
import type React from 'react';
import type { FormElement } from '@/formbuilder/formBuilder.constants';

type DraggableCollapsibleFormBlockProps = Omit<React.ComponentProps<typeof CollapsibleFormBlock>, 'children'> & {
  components: FormElement[];
  index: number;
  onSearchMove: (dragIndex: number, hoverIndex: number) => void;
  dragType: string;
  enableDrag?: boolean;
  revealRequestId?: string;
  onRevealComplete?: (searchId: string | undefined) => void;
};

export const DraggableCollapsibleFormBlock = ({
  components,
  index,
  onSearchMove,
  dragType,
  enableDrag = true,
  revealRequestId,
  onRevealComplete,
  ...rest
}: DraggableCollapsibleFormBlockProps) => {
  const [containerNode, setContainerNode] = useState<HTMLDivElement | null>(null);
  const containerNodeRef = useRef<HTMLDivElement | null>(null);
  const isPointerDownOnInputRef = useRef(false);

  const canDrag = useCallback(() => enableDrag && !isPointerDownOnInputRef.current, [enableDrag]);
  const { ref, isDragging } = useDraggableSearch(index, onSearchMove, dragType, canDrag);

  const setCombinedRef = (node: HTMLDivElement | null) => {
    containerNodeRef.current = node;
    setContainerNode(node);
    ref(node);
  };

  useEffect(() => {
    if (!containerNode) {
      return;
    }

    const isTextInputElement = (element: HTMLElement | null) => {
      if (!element) {
        return false;
      }

      const tagName = element.tagName;
      return tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT' || element.isContentEditable;
    };

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      isPointerDownOnInputRef.current = !!target && containerNode.contains(target) && isTextInputElement(target);
    };

    const handlePointerUp = () => {
      isPointerDownOnInputRef.current = false;
    };

    containerNode.addEventListener('pointerdown', handlePointerDown, true);
    containerNode.addEventListener('pointerup', handlePointerUp, true);
    containerNode.addEventListener('pointercancel', handlePointerUp, true);
    containerNode.addEventListener('pointerleave', handlePointerUp, true);

    return () => {
      containerNode.removeEventListener('pointerdown', handlePointerDown, true);
      containerNode.removeEventListener('pointerup', handlePointerUp, true);
      containerNode.removeEventListener('pointercancel', handlePointerUp, true);
      containerNode.removeEventListener('pointerleave', handlePointerUp, true);
    };
  }, [containerNode]);

  useEffect(() => {
    const node = containerNodeRef.current;
    if (!node || !revealRequestId || !onRevealComplete) {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      scrollToTopOfScrollParent(node);
      onRevealComplete(undefined);
    });

    return () => cancelAnimationFrame(frameId);
  }, [onRevealComplete, revealRequestId]);

  const children = components.map((child) => renderIt(child));

  return (
    <CollapsibleFormBlock
      {...rest}
      dragRef={enableDrag ? setCombinedRef : undefined}
      isDragging={enableDrag ? isDragging : false}>
      {children}
    </CollapsibleFormBlock>
  );
};
